import {
  DAY_HOUR_TIME_PARSE_FORMAT,
  TWELVE_HOUR_TIME_PARSE_FORMAT,
} from 'features/store/constants';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import {
  getNextEarliestOpeningHours,
  isStoreClosingWithinMinutes,
  readableCloseHourToday,
  readableHour,
} from 'features/store/utils';
import { useIntl } from 'react-intl';

import { IRestaurant } from '@rbi-ctg/store';
import { getConfigValue } from 'utils/environment';

interface IOpenClosedText {
  isOpen: boolean;
  isWithinOneHourOfClose: boolean;
  text: string;
}

export const useOpenClosedText = (store: IRestaurant): IOpenClosedText => {
  const todayFormatConfig = getConfigValue({
    key: 'timeFormat',
    defaultValue: TWELVE_HOUR_TIME_PARSE_FORMAT,
  });
  const futureFormatConfig = DAY_HOUR_TIME_PARSE_FORMAT;
  const { getPickupOpenServiceModes } = useStoreAvailabilityFns();
  const { formatMessage } = useIntl();
  const openPickupServiceModes = getPickupOpenServiceModes(store);

  // this means that the store is not open for pickup at all
  if (!openPickupServiceModes.length) {
    const nextEarliestOpeningTimes = getNextEarliestOpeningHours({
      store,
      onlyPickUpServiceModes: true,
    });

    if (!nextEarliestOpeningTimes) {
      return {
        isOpen: false,
        isWithinOneHourOfClose: false,
        text: formatMessage({ id: 'closed' }),
      };
    }

    const { open: openingTime, nextOpen: nextOpeningTime } = nextEarliestOpeningTimes;

    const timeFormat =
      store?.localTime != null && openingTime != null && store.localTime > openingTime
        ? futureFormatConfig
        : todayFormatConfig;

    const timeISOString =
      store?.localTime != null && openingTime != null && store.localTime > openingTime
        ? nextOpeningTime
        : openingTime ?? nextOpeningTime;

    if (!timeISOString) {
      return {
        isOpen: false,
        isWithinOneHourOfClose: false,
        text: `${formatMessage({ id: 'closed' })}`,
      };
    }

    const readable = readableHour({
      timeISOString,
      timeFormat,
      storeTimeZone: store.timezone ?? '',
    });
    return {
      isOpen: false,
      isWithinOneHourOfClose: false,
      text: `${formatMessage({ id: 'closed' })} - ${formatMessage(
        { id: 'opensAt' },
        {
          time: readable ?? formatMessage({ id: 'notApplicable' }),
        }
      )}`,
    };
  }

  const isClosingWithinNextHour = isStoreClosingWithinMinutes(store, 60);

  if (isClosingWithinNextHour) {
    return {
      isOpen: true,
      isWithinOneHourOfClose: true,
      text: formatMessage(
        { id: 'closesAt' },
        {
          time: readableCloseHourToday(store, todayFormatConfig),
        }
      ),
    };
  }

  return {
    isOpen: true,
    isWithinOneHourOfClose: false,
    text: formatMessage({ id: 'open' }),
  };
};
